import React from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import {
  ErpEmpty,
  ErpLoading,
  ErpPanel,
  ErpSectionTitle,
  MovementStatusChip,
} from "../../../components/dashboard/erp/ErpUi";
import {
  ERP_BRAND,
  MOVEMENT_KIND_LABELS,
  formatDate,
  formatDateTime,
  formatMoney,
  toDateInput,
} from "../../../utils/erp";
import {
  hasAnyProjectPermission,
  hasProjectPermission,
} from "../../../utils/projectAccess";

const PROFIT = "#159455";
const LOSS = "#C83A3A";
const COST = "#F08A3E";
const BLUE = "#2F80ED";
const PURPLE = "#7657D5";

function startOfMonth() {
  const now = new Date();
  return toDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
}

function rangeForPreset(preset) {
  const now = new Date();
  const today = toDateInput(now);
  if (preset === "today") return { preset, dateFrom: today, dateTo: today };
  if (preset === "week") {
    const from = new Date(now);
    from.setDate(now.getDate() - 6);
    return { preset, dateFrom: toDateInput(from), dateTo: today };
  }
  if (preset === "30days") {
    const from = new Date(now);
    from.setDate(now.getDate() - 29);
    return { preset, dateFrom: toDateInput(from), dateTo: today };
  }
  return { preset: "month", dateFrom: startOfMonth(), dateTo: today };
}

function formatPercent(value, decimals = 1) {
  const number = Number(value || 0);
  return `${number.toLocaleString("es-AR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
}

function formatReportDate(value) {
  if (!value) return "—";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
      .format(new Date(`${raw}T12:00:00`));
  }
  return formatDate(value);
}

function comparisonText(metric, { inverse = false } = {}) {
  if (!metric || metric.percent === null || metric.percent === undefined) {
    return { label: "Sin base comparable", favorable: null, direction: "flat" };
  }
  const percent = Number(metric.percent || 0);
  const favorable = inverse ? percent <= 0 : percent >= 0;
  return {
    label: `${Math.abs(percent).toLocaleString("es-AR", { maximumFractionDigits: 1 })}% vs. período anterior`,
    favorable,
    direction: percent > 0 ? "up" : percent < 0 ? "down" : "flat",
  };
}

function ComparisonChip({ metric, inverse = false, sx = {} }) {
  const comparison = comparisonText(metric, { inverse });
  const icon = comparison.direction === "up"
    ? <ArrowUpwardRoundedIcon />
    : comparison.direction === "down"
      ? <ArrowDownwardRoundedIcon />
      : <ShowChartRoundedIcon />;
  return (
    <Chip
      size="small"
      icon={icon}
      label={comparison.label}
      sx={{
        fontWeight: 850,
        bgcolor: comparison.favorable === null
          ? "rgba(25,36,47,0.07)"
          : comparison.favorable
            ? "rgba(21,148,85,0.11)"
            : "rgba(200,58,58,0.1)",
        color: comparison.favorable === null
          ? ERP_BRAND.muted
          : comparison.favorable
            ? PROFIT
            : LOSS,
        height: "auto",
        maxWidth: "100%",
        "& .MuiChip-icon": { color: "inherit", fontSize: 17 },
        "& .MuiChip-label": {
          display: "block",
          py: 0.45,
          whiteSpace: "normal",
          lineHeight: 1.25,
        },
        ...sx,
      }}
    />
  );
}

function Definition({ text }) {
  return (
    <Tooltip title={text} arrow placement="top">
      <InfoOutlinedIcon sx={{ fontSize: 17, color: "rgba(25,36,47,0.42)", cursor: "help" }} />
    </Tooltip>
  );
}

function SummaryMetric({ icon, label, value, description, metric, inverse = false, tone = "blue" }) {
  const tones = {
    green: { color: PROFIT, bg: "rgba(21,148,85,0.1)" },
    orange: { color: COST, bg: "rgba(240,138,62,0.11)" },
    blue: { color: BLUE, bg: "rgba(47,128,237,0.1)" },
    purple: { color: PURPLE, bg: "rgba(118,87,213,0.1)" },
    red: { color: LOSS, bg: "rgba(200,58,58,0.1)" },
  };
  const selected = tones[tone] || tones.blue;
  return (
    <ErpPanel sx={{ height: "100%", transition: "transform .18s ease, box-shadow .18s ease", "&:hover": { transform: "translateY(-2px)", boxShadow: "0 16px 34px rgba(25,36,47,0.09)" } }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 900, letterSpacing: 0.5 }}>
              {label}
            </Typography>
            <Typography variant="h5" sx={{ color: ERP_BRAND.dark, fontWeight: 950, mt: 0.65, fontSize: { xs: "1.22rem", sm: "1.5rem" }, overflowWrap: "anywhere" }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ width: 44, height: 44, borderRadius: 3, display: "grid", placeItems: "center", color: selected.color, bgcolor: selected.bg, flexShrink: 0 }}>
            {icon}
          </Box>
        </Stack>
        <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 1, minHeight: { xs: 0, sm: 40 }, lineHeight: 1.5 }}>
          {description}
        </Typography>
        <ComparisonChip metric={metric} inverse={inverse} sx={{ mt: 1.2 }} />
      </CardContent>
    </ErpPanel>
  );
}

function PositionCard({ icon, label, value, detail, tone = "blue" }) {
  const colors = { blue: BLUE, green: PROFIT, orange: COST, red: LOSS, purple: PURPLE, dark: ERP_BRAND.dark };
  const color = colors[tone] || BLUE;
  return (
    <Box sx={{ p: { xs: 1.7, sm: 2.1 }, borderRadius: 3.2, bgcolor: "#fff", border: `1px solid ${ERP_BRAND.border}`, height: "100%", minWidth: 0 }}>
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box sx={{ width: 38, height: 38, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: `${color}16`, color }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850 }}>{label}</Typography>
          <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, fontSize: 18 }}>{value}</Typography>
        </Box>
      </Stack>
      <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, mt: 1.2, lineHeight: 1.45 }}>
        {detail}
      </Typography>
    </Box>
  );
}

function AccountBreakdown({ title, description, rows, total, color, emptyText, currency = "ARS" }) {
  const max = Math.max(1, ...rows.map((row) => Math.abs(Number(row.amount || 0))));
  return (
    <ErpPanel sx={{ height: "100%" }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <ErpSectionTitle title={title} description={description} />
        {!rows.length ? (
          <ErpEmpty title={emptyText} description="No hay cuentas con movimiento dentro del período seleccionado." />
        ) : (
          <Stack spacing={2.1} sx={{ mt: 2.4 }}>
            {rows.slice(0, 6).map((row) => (
              <Box key={row.code}>
                <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>{row.name}</Typography>
                    <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{row.code} · {row.entries || 0} líneas</Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, flexShrink: 0 }}>{formatMoney(row.amount, currency)}</Typography>
                </Stack>
                <Box sx={{ height: 7, borderRadius: 999, bgcolor: "rgba(25,36,47,0.07)", overflow: "hidden", mt: 1 }}>
                  <Box sx={{ height: "100%", width: `${Math.max(3, (Math.abs(Number(row.amount || 0)) / max) * 100)}%`, borderRadius: 999, bgcolor: color }} />
                </Box>
              </Box>
            ))}
            <Divider />
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Typography sx={{ fontWeight: 900, color: ERP_BRAND.muted }}>Total</Typography>
              <Typography sx={{ fontWeight: 950, color }}>{formatMoney(total, currency)}</Typography>
            </Stack>
          </Stack>
        )}
      </CardContent>
    </ErpPanel>
  );
}

function TrendChart({ data = [], currency = "ARS" }) {
  if (!data.length) {
    return <ErpEmpty title="Todavía no hay tendencia" description="Cuando registres ingresos y gastos aparecerá la evolución diaria del resultado." />;
  }

  const width = 760;
  const height = 250;
  const left = 20;
  const right = 18;
  const top = 22;
  const bottom = 45;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const maxValue = Math.max(1, ...data.flatMap((point) => [Math.abs(Number(point.income || 0)), Math.abs(Number(point.expenses || 0))]));
  const x = (index) => left + (data.length === 1 ? chartWidth / 2 : (index / (data.length - 1)) * chartWidth);
  const y = (value) => top + chartHeight - (Math.max(0, Number(value || 0)) / maxValue) * chartHeight;
  const incomePoints = data.map((point, index) => `${x(index)},${y(point.income)}`).join(" ");
  const expensePoints = data.map((point, index) => `${x(index)},${y(point.expenses)}`).join(" ");
  const labelIndexes = [...new Set([0, Math.floor((data.length - 1) / 2), data.length - 1])];

  return (
    <Box>
      <Stack direction="row" spacing={2.2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Stack direction="row" spacing={0.8} alignItems="center"><Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: PROFIT }} /><Typography variant="caption" sx={{ fontWeight: 850 }}>Ingresos</Typography></Stack>
        <Stack direction="row" spacing={0.8} alignItems="center"><Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: COST }} /><Typography variant="caption" sx={{ fontWeight: 850 }}>Costos y gastos</Typography></Stack>
      </Stack>
      <Box sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch", pb: 0.5 }}>
        <Box component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", minWidth: { xs: 500, sm: 570 }, display: "block" }} aria-label="Evolución diaria de ingresos y gastos">
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const lineY = top + chartHeight * ratio;
            return <line key={ratio} x1={left} y1={lineY} x2={width - right} y2={lineY} stroke="rgba(25,36,47,0.09)" strokeDasharray="4 5" />;
          })}
          <polyline points={incomePoints} fill="none" stroke={PROFIT} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <polyline points={expensePoints} fill="none" stroke={COST} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          {data.length <= 31 ? data.map((point, index) => (
            <React.Fragment key={point.date}>
              <circle cx={x(index)} cy={y(point.income)} r="4" fill="#fff" stroke={PROFIT} strokeWidth="3" />
              <circle cx={x(index)} cy={y(point.expenses)} r="4" fill="#fff" stroke={COST} strokeWidth="3" />
            </React.Fragment>
          )) : null}
          {labelIndexes.map((index) => (
            <text key={index} x={x(index)} y={height - 15} textAnchor="middle" fontSize="12" fill="rgba(25,36,47,0.58)">
              {new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short" }).format(new Date(`${data[index].date}T12:00:00`))}
            </text>
          ))}
        </Box>
      </Box>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Escala máxima: {formatMoney(maxValue, currency)}</Typography>
        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Cada punto representa el total contabilizado del día.</Typography>
      </Stack>
    </Box>
  );
}

function HealthAlert({ severity, icon, title, description, action }) {
  return (
    <Alert
      severity={severity}
      icon={icon}
      action={action}
      sx={{ borderRadius: 3, alignItems: "center", "& .MuiAlert-message": { width: "100%" } }}
    >
      <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }}>{description}</Typography>
    </Alert>
  );
}

const BUSINESS_HEALTH_AREAS = [
  { key: "sales", label: "Ventas", icon: <ShoppingCartCheckoutRoundedIcon /> },
  { key: "expenses", label: "Gastos y margen", icon: <ReceiptLongRoundedIcon /> },
  { key: "cash", label: "Caja", icon: <AccountBalanceWalletRoundedIcon /> },
  { key: "debt", label: "Deuda", icon: <TrendingDownRoundedIcon /> },
  { key: "stock", label: "Stock", icon: <Inventory2RoundedIcon /> },
  { key: "activity", label: "Actividad", icon: <AssessmentRoundedIcon /> },
];

const BUSINESS_HEALTH_STATUS = {
  healthy: {
    label: "Saludable",
    severity: "success",
    color: PROFIT,
    background: "rgba(21,148,85,0.09)",
    icon: <CheckCircleRoundedIcon />,
  },
  warning: {
    label: "Requiere atención",
    severity: "warning",
    color: COST,
    background: "rgba(240,138,62,0.1)",
    icon: <ErrorOutlineRoundedIcon />,
  },
  critical: {
    label: "Estado crítico",
    severity: "error",
    color: LOSS,
    background: "rgba(200,58,58,0.09)",
    icon: <ErrorOutlineRoundedIcon />,
  },
  insufficient_data: {
    label: "Datos insuficientes",
    severity: "info",
    color: BLUE,
    background: "rgba(47,128,237,0.09)",
    icon: <InfoOutlinedIcon />,
  },
};

function businessHealthStatus(status) {
  return BUSINESS_HEALTH_STATUS[status] || BUSINESS_HEALTH_STATUS.insufficient_data;
}

function optionalPercent(value) {
  if (value === null || value === undefined || value === "") return "Sin base comparable";
  return formatPercent(value);
}

function optionalDays(value) {
  if (value === null || value === undefined || value === "") return "Sin base de gastos";
  const number = Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 1 });
  return `${number} días`;
}

function hasMetric(metrics, key) {
  return Object.prototype.hasOwnProperty.call(metrics || {}, key)
    && metrics[key] !== undefined;
}

function finiteScore(value) {
  return value !== null && value !== undefined && Number.isFinite(Number(value));
}

function healthMetricRows(key, metrics = {}, currency = "ARS") {
  const rows = {
    sales: [
      hasMetric(metrics, "revenue") ? ["Ventas netas", formatMoney(metrics.revenue, currency)] : null,
      hasMetric(metrics, "changePercent") ? ["Variación", optionalPercent(metrics.changePercent)] : null,
      hasMetric(metrics, "incomeEntries") ? ["Movimientos de ingreso", Number(metrics.incomeEntries || 0).toLocaleString("es-AR")] : null,
    ],
    expenses: [
      hasMetric(metrics, "totalExpenses") ? ["Gastos totales", formatMoney(metrics.totalExpenses, currency)] : null,
      hasMetric(metrics, "expenseRatioPercent") ? ["Gastos / ventas", optionalPercent(metrics.expenseRatioPercent)] : null,
      hasMetric(metrics, "netMarginPercent") ? ["Margen neto", optionalPercent(metrics.netMarginPercent)] : null,
    ],
    cash: [
      hasMetric(metrics, "cash") ? ["Disponible", formatMoney(metrics.cash, currency)] : null,
      hasMetric(metrics, "cashRunwayDays") ? ["Autonomía estimada", optionalDays(metrics.cashRunwayDays)] : null,
      hasMetric(metrics, "cashCoveragePercent") ? ["Cobertura de cuentas por pagar", optionalPercent(metrics.cashCoveragePercent)] : null,
    ],
    debt: [
      hasMetric(metrics, "debt") ? ["Deuda total", formatMoney(metrics.debt, currency)] : null,
      hasMetric(metrics, "netDebt") ? ["Deuda neta de caja", formatMoney(metrics.netDebt, currency)] : null,
      hasMetric(metrics, "debtToRevenuePercent") ? ["Deuda / ventas", optionalPercent(metrics.debtToRevenuePercent)] : null,
    ],
    stock: [
      hasMetric(metrics, "trackedItems") ? ["Ítems controlados", Number(metrics.trackedItems || 0).toLocaleString("es-AR")] : null,
      hasMetric(metrics, "outOfStockItems") ? ["Agotados", Number(metrics.outOfStockItems || 0).toLocaleString("es-AR")] : null,
      hasMetric(metrics, "lowStockItems") ? ["En reposición", Number(metrics.lowStockItems || 0).toLocaleString("es-AR")] : null,
    ],
    activity: [
      hasMetric(metrics, "events") ? ["Eventos", Number(metrics.events || 0).toLocaleString("es-AR")] : null,
      hasMetric(metrics, "operationalEvents") ? ["Eventos operativos", Number(metrics.operationalEvents || 0).toLocaleString("es-AR")] : null,
      hasMetric(metrics, "lastAt") ? ["Última actividad", metrics.lastAt ? formatDateTime(metrics.lastAt) : "Sin actividad registrada"] : null,
    ],
  };
  return (rows[key] || []).filter(Boolean);
}

function extractBusinessHealth(payload) {
  const candidates = [
    payload?.businessHealth,
    payload?.health,
    payload?.operationalHealth,
    payload?.overview?.businessHealth,
    payload?.overview?.health,
    payload,
  ];
  return candidates.find((candidate) => candidate?.indicators && candidate?.summary) || null;
}

function BusinessHealthIndicatorCard({ definition, indicator, currency }) {
  const status = businessHealthStatus(indicator?.status);
  const alerts = Array.isArray(indicator?.alerts) ? indicator.alerts : [];
  const metrics = healthMetricRows(definition.key, indicator?.metrics, currency);

  return (
    <ErpPanel sx={{ height: "100%", borderTop: `4px solid ${status.color}` }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.4 } }}>
        <Stack direction="row" justifyContent="space-between" spacing={1.4} alignItems="flex-start">
          <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2.7, display: "grid", placeItems: "center", color: status.color, bgcolor: status.background, flexShrink: 0 }}>
              {definition.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 950 }}>{indicator?.label || definition.label}</Typography>
              {finiteScore(indicator?.score) ? (
                <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 800 }}>
                  Puntaje {Number(indicator.score).toLocaleString("es-AR")}/100
                </Typography>
              ) : null}
            </Box>
          </Stack>
          <Chip size="small" color={status.severity} label={status.label} sx={{ fontWeight: 900, flexShrink: 0 }} />
        </Stack>

        <Typography variant="body2" sx={{ mt: 1.5, color: ERP_BRAND.muted, lineHeight: 1.55 }}>
          {indicator?.summary || "El servicio no devolvió un resumen para esta área."}
        </Typography>

        {metrics.length ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 0.8, mt: 1.7 }}>
            {metrics.map(([label, value]) => (
              <Box key={label} sx={{ p: 1.15, borderRadius: 2.3, bgcolor: ERP_BRAND.soft, minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, fontWeight: 850, lineHeight: 1.25 }}>{label}</Typography>
                <Typography sx={{ mt: 0.45, color: ERP_BRAND.dark, fontWeight: 900, fontSize: 13.5, overflowWrap: "anywhere" }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        ) : null}

        <Stack spacing={1} sx={{ mt: 1.7 }}>
          {alerts.length ? alerts.map((alert) => {
            const alertStatus = businessHealthStatus(alert.severity === "critical" ? "critical" : "warning");
            return (
              <Box key={alert.code || `${definition.key}-${alert.title}`} sx={{ p: 1.35, borderRadius: 2.5, bgcolor: alertStatus.background, borderLeft: `4px solid ${alertStatus.color}` }}>
                <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 900, fontSize: 14 }}>{alert.title}</Typography>
                {alert.message ? <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35, lineHeight: 1.45 }}>{alert.message}</Typography> : null}
                {alert.cause ? (
                  <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.dark, mt: 0.7, lineHeight: 1.4 }}>
                    <Box component="span" sx={{ fontWeight: 950 }}>Causa detectada: </Box>{alert.cause}
                  </Typography>
                ) : null}
              </Box>
            );
          }) : indicator?.status === "healthy" ? (
            <Alert severity="success" sx={{ borderRadius: 2.5 }}>
              No se detectaron alertas para esta área con los datos analizados.
            </Alert>
          ) : indicator?.status === "insufficient_data" ? (
            <Alert severity="info" sx={{ borderRadius: 2.5 }}>
              No hay datos suficientes para emitir una conclusión en esta área.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2.5 }}>
              El servicio indicó este estado sin detallar una alerta. No se agrega una explicación no provista por los datos.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </ErpPanel>
  );
}

function BusinessHealthCenter({ health, error, currency = "ARS" }) {
  if (!health) {
    return (
      <ErpPanel>
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <ErpSectionTitle
            title="Centro de salud del negocio"
            description="Diagnóstico explicable de ventas, gastos, caja, deuda, stock y actividad."
          />
          <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 900 }}>El diagnóstico operativo no está disponible</Typography>
            <Typography variant="body2" sx={{ mt: 0.35 }}>
              {error || "La respuesta actual no incluyó indicadores del servicio de salud. No se muestran recomendaciones ni conclusiones sin datos."}
            </Typography>
          </Alert>
        </CardContent>
      </ErpPanel>
    );
  }

  const status = businessHealthStatus(health.status);
  const summary = health.summary || {};
  const reportCurrency = health.currency || currency;
  const reportPeriod = health.period
    ? `${formatReportDate(health.period.from)} al ${formatReportDate(health.period.to)}`
    : "Período no informado";

  return (
    <Stack spacing={2}>
      <ErpPanel sx={{ overflow: "hidden" }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.7, md: 3.2 }, bgcolor: status.background }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2.2} alignItems={{ xs: "stretch", md: "center" }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ width: 48, height: 48, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "#fff", color: status.color, boxShadow: "0 8px 20px rgba(25,36,47,0.08)", flexShrink: 0 }}>
                {status.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ color: ERP_BRAND.dark, fontWeight: 950 }}>Centro de salud del negocio</Typography>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.45, lineHeight: 1.5 }}>
                  El diagnóstico usa datos registrados y muestra la causa calculada de cada alerta. No genera recomendaciones cuando faltan evidencias.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip color={status.severity} label={status.label} sx={{ fontWeight: 950 }} />
              {finiteScore(health.score) ? <Chip label={`Puntaje ${Number(health.score).toLocaleString("es-AR")}/100`} sx={{ bgcolor: "#fff", fontWeight: 900 }} /> : null}
              <Chip label={reportPeriod} variant="outlined" sx={{ bgcolor: "rgba(255,255,255,0.55)", fontWeight: 850 }} />
            </Stack>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1, mt: 2.2 }}>
            {[
              ["Áreas evaluadas", `${Number(summary.availableIndicators || 0)}/${Number(summary.indicators || BUSINESS_HEALTH_AREAS.length)}`],
              ["Alertas críticas", Number(summary.criticalAlerts || 0).toLocaleString("es-AR")],
              ["Advertencias", Number(summary.warningAlerts || 0).toLocaleString("es-AR")],
              ["Alertas totales", Number(summary.totalAlerts || 0).toLocaleString("es-AR")],
            ].map(([label, value]) => (
              <Box key={label} sx={{ p: 1.3, borderRadius: 2.6, bgcolor: "rgba(255,255,255,0.68)", border: "1px solid rgba(25,36,47,0.06)" }}>
                <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, fontWeight: 850 }}>{label}</Typography>
                <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 950, fontSize: 19, mt: 0.25 }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </ErpPanel>

      <Grid container spacing={2}>
        {BUSINESS_HEALTH_AREAS.map((definition) => (
          <Grid key={definition.key} item xs={12} md={6} xl={4}>
            <BusinessHealthIndicatorCard
              definition={definition}
              indicator={health.indicators?.[definition.key] || {
                label: definition.label,
                status: "insufficient_data",
                summary: "El servicio no devolvió datos para esta área.",
                metrics: {},
                alerts: [],
              }}
              currency={reportCurrency}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

function accountingDayInput(value) {
  if (!value) return "";
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : toDateInput(value);
}

function formatAccountingDay(value) {
  const day = accountingDayInput(value);
  if (!day) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(`${day}T12:00:00`));
}

function actorLabel(actor) {
  return actor?.name || actor?.email || "Usuario no identificado";
}

function AccountingPeriodControl({ projectId, access }) {
  const canManage = hasProjectPermission(access, "erp.ledger.manage")
    && hasProjectPermission(access, "project.settings.manage");
  const [period, setPeriod] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [action, setAction] = React.useState("");
  const [form, setForm] = React.useState({ date: "", reason: "" });

  const loadPeriod = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.getAccountingPeriod(projectId, { historyLimit: 5 });
      setPeriod(data.accountingPeriod || null);
    } catch (err) {
      setError(err.message || "No se pudo consultar el período contable");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    loadPeriod();
  }, [loadPeriod]);

  const openAction = (nextAction) => {
    setError("");
    setSuccess("");
    setAction(nextAction);
    setForm({
      date: nextAction === "close"
        ? toDateInput(new Date())
        : accountingDayInput(period?.lastAccountingClosedDate),
      reason: "",
    });
  };

  const closeAction = () => {
    if (saving) return;
    setAction("");
    setError("");
  };

  const submitAction = async () => {
    const date = String(form.date || "").trim();
    const reason = String(form.reason || "").trim();
    if (!date) {
      setError(action === "close" ? "Indicá hasta qué día querés cerrar." : "Indicá desde qué día querés reabrir.");
      return;
    }
    if (action === "reopen" && !reason) {
      setError("El motivo de reapertura es obligatorio.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      const data = action === "close"
        ? await erpApi.closeAccountingPeriod(projectId, {
          closedThrough: date,
          note: reason,
        })
        : await erpApi.reopenAccountingPeriod(projectId, {
          reopenFrom: date,
          reason,
        });
      setPeriod(data.accountingPeriod || period);
      setSuccess(
        action === "close"
          ? data.changed === false
            ? `El período ya estaba cerrado hasta ${formatAccountingDay(date)}.`
            : `Período cerrado correctamente hasta ${formatAccountingDay(date)}.`
          : `Período reabierto desde ${formatAccountingDay(date)}.`,
      );
      setAction("");
    } catch (err) {
      setError(err.message || "No se pudo actualizar el período contable");
    } finally {
      setSaving(false);
    }
  };

  const hasClosedPeriod = period?.hasClosedPeriod === true;
  const closedThrough = accountingDayInput(period?.lastAccountingClosedDate);
  const dialogIsClose = action === "close";

  return (
    <>
      <ErpPanel>
        {loading ? <LinearProgress /> : null}
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <ErpSectionTitle
            title="Control del período contable"
            description="Consultá el último día cerrado y administrá reaperturas autorizadas sin modificar movimientos históricos."
            action={(
              <Button
                size="small"
                startIcon={<RefreshRoundedIcon />}
                onClick={loadPeriod}
                disabled={loading || saving}
                sx={{ textTransform: "none", fontWeight: 850 }}
              >
                Consultar
              </Button>
            )}
          />

          {error && !action ? <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>{error}</Alert> : null}
          {success ? <Alert severity="success" sx={{ mt: 2, borderRadius: 3 }} onClose={() => setSuccess("")}>{success}</Alert> : null}

          {!loading && period ? (
            <Stack spacing={2.1} sx={{ mt: 2.3 }}>
              <Box
                sx={{
                  p: { xs: 1.8, sm: 2.2 },
                  borderRadius: 3.2,
                  border: `1px solid ${ERP_BRAND.border}`,
                  bgcolor: hasClosedPeriod ? "rgba(240,138,62,0.07)" : "rgba(21,148,85,0.07)",
                }}
              >
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
                  <Stack direction="row" spacing={1.4} alignItems="center">
                    <Box sx={{ width: 44, height: 44, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: hasClosedPeriod ? "rgba(240,138,62,0.14)" : "rgba(21,148,85,0.13)", color: hasClosedPeriod ? COST : PROFIT, flexShrink: 0 }}>
                      {hasClosedPeriod ? <LockRoundedIcon /> : <LockOpenRoundedIcon />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Chip
                        size="small"
                        color={hasClosedPeriod ? "warning" : "success"}
                        label={hasClosedPeriod ? `Cerrado hasta ${formatAccountingDay(period.lastAccountingClosedDate)}` : "Sin cierre contable vigente"}
                        sx={{ fontWeight: 900 }}
                      />
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.7, lineHeight: 1.5 }}>
                        {hasClosedPeriod
                          ? `Las operaciones con fecha igual o anterior al ${formatAccountingDay(period.lastAccountingClosedDate)} quedan protegidas. El primer día abierto es ${formatAccountingDay(period.nextOpenDate)}.`
                          : "Todas las fechas contables permanecen abiertas para movimientos autorizados."}
                      </Typography>
                    </Box>
                  </Stack>

                  {canManage ? (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ flexShrink: 0 }}>
                      <Button
                        variant="contained"
                        startIcon={<LockRoundedIcon />}
                        onClick={() => openAction("close")}
                        disabled={saving}
                        sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2.5 }}
                      >
                        Cerrar período
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<LockOpenRoundedIcon />}
                        onClick={() => openAction("reopen")}
                        disabled={saving || !hasClosedPeriod}
                        sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2.5 }}
                      >
                        Reabrir
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" }, gap: 1.3 }}>
                <Box sx={{ p: 1.6, borderRadius: 2.8, bgcolor: ERP_BRAND.soft }}>
                  <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850 }}>ÚLTIMO CIERRE</Typography>
                  <Typography sx={{ mt: 0.45, fontWeight: 900, color: ERP_BRAND.dark }}>{hasClosedPeriod ? formatAccountingDay(period.lastAccountingClosedDate) : "No realizado"}</Typography>
                  <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, mt: 0.35 }}>{period.closedAt ? `${actorLabel(period.closedBy)} · ${formatDateTime(period.closedAt)}` : "Sin responsable registrado"}</Typography>
                </Box>
                <Box sx={{ p: 1.6, borderRadius: 2.8, bgcolor: ERP_BRAND.soft }}>
                  <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850 }}>PRIMER DÍA ABIERTO</Typography>
                  <Typography sx={{ mt: 0.45, fontWeight: 900, color: ERP_BRAND.dark }}>{hasClosedPeriod ? formatAccountingDay(period.nextOpenDate) : "Todo el período"}</Typography>
                  <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, mt: 0.35 }}>Desde esta fecha pueden confirmarse nuevas operaciones.</Typography>
                </Box>
                <Box sx={{ p: 1.6, borderRadius: 2.8, bgcolor: ERP_BRAND.soft, gridColumn: { sm: "1 / -1", lg: "auto" } }}>
                  <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850 }}>ÚLTIMA REAPERTURA</Typography>
                  <Typography sx={{ mt: 0.45, fontWeight: 900, color: ERP_BRAND.dark }}>{period.reopenedAt ? formatDateTime(period.reopenedAt) : "No realizada"}</Typography>
                  <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, mt: 0.35 }}>{period.reopenedAt ? `${actorLabel(period.reopenedBy)}${period.reopenReason ? ` · ${period.reopenReason}` : ""}` : "Sin reaperturas registradas"}</Typography>
                </Box>
              </Box>

              {!canManage ? (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  Podés consultar el cierre, pero para cerrarlo o reabrirlo necesitás administrar el libro contable y la configuración del proyecto.
                </Alert>
              ) : null}
            </Stack>
          ) : null}
        </CardContent>
      </ErpPanel>

      <Dialog open={Boolean(action)} onClose={saving ? undefined : closeAction} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>
          {dialogIsClose ? "Cerrar período contable" : "Reabrir período contable"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity={dialogIsClose ? "warning" : "info"} sx={{ borderRadius: 3 }}>
              {dialogIsClose
                ? "Las operaciones con fecha igual o anterior al día elegido quedarán bloqueadas para confirmación, anulación o corrección."
                : `La reapertura mueve el cierre al día anterior a la fecha elegida. El cierre vigente llega hasta ${formatAccountingDay(period?.lastAccountingClosedDate)}.`}
            </Alert>
            <TextField
              fullWidth
              required
              type="date"
              label={dialogIsClose ? "Cerrar hasta" : "Reabrir desde"}
              value={form.date}
              onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: dialogIsClose ? toDateInput(new Date()) : closedThrough || undefined,
                min: dialogIsClose ? closedThrough || undefined : undefined,
              }}
            />
            <TextField
              fullWidth
              required={!dialogIsClose}
              multiline
              minRows={3}
              label={dialogIsClose ? "Nota del cierre (opcional)" : "Motivo de reapertura"}
              value={form.reason}
              onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
              inputProps={{ maxLength: 1000 }}
              helperText={dialogIsClose ? "La nota queda registrada en el historial contable." : "El motivo es obligatorio y queda asociado al usuario que autoriza la reapertura."}
            />
            {error && action ? <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeAction} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
          <Button
            variant="contained"
            color={dialogIsClose ? "warning" : "primary"}
            startIcon={dialogIsClose ? <LockRoundedIcon /> : <LockOpenRoundedIcon />}
            onClick={submitAction}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            {saving ? "Guardando..." : dialogIsClose ? "Confirmar cierre" : "Confirmar reapertura"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function ProjectErpOverviewPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { bootstrap, access } = useOutletContext();
  const canReadCash = hasProjectPermission(access, "cash.registers.read");
  const canReadLedger = hasProjectPermission(access, "erp.ledger.read");
  const canCreateMovement = hasAnyProjectPermission(access, [
    "erp.sales.create",
    "erp.purchases.create",
    "erp.expenses.create",
    "erp.internal_consumption.create",
    "erp.stock.manage",
  ]);
  const [overview, setOverview] = React.useState(null);
  const [businessHealth, setBusinessHealth] = React.useState(null);
  const [healthError, setHealthError] = React.useState("");
  const [registers, setRegisters] = React.useState([]);
  const [range, setRange] = React.useState(() => rangeForPreset("month"));
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setHealthError("");
      setBusinessHealth(null);
      const overviewData = await erpApi.overview(projectId, {
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
      });
      setOverview(overviewData.overview || null);

      const embeddedHealth = extractBusinessHealth(overviewData);
      const requests = [
        embeddedHealth
          ? Promise.resolve({ businessHealth: embeddedHealth })
          : erpApi.businessHealth(projectId, {
            dateFrom: range.dateFrom,
            dateTo: range.dateTo,
          }),
        canReadCash ? erpApi.listCashRegisters(projectId) : Promise.resolve({ registers: [] }),
      ];
      const [healthResult, cashResult] = await Promise.allSettled(requests);

      if (healthResult.status === "fulfilled") {
        const nextHealth = extractBusinessHealth(healthResult.value);
        setBusinessHealth(nextHealth);
        if (!nextHealth) {
          setHealthError("El servicio respondió sin el contrato de indicadores esperado.");
        }
      } else {
        setHealthError(healthResult.reason?.message || "No se pudo consultar el diagnóstico operativo.");
      }

      if (cashResult.status === "rejected") throw cashResult.reason;
      setRegisters(cashResult.value?.registers || []);
    } catch (err) {
      setError(err.message || "No se pudo cargar el resumen contable");
    } finally {
      setLoading(false);
    }
  }, [canReadCash, projectId, range.dateFrom, range.dateTo]);

  React.useEffect(() => {
    load();
  }, [load]);

  const selectPreset = (preset) => {
    if (preset === "custom") setRange((current) => ({ ...current, preset }));
    else setRange(rangeForPreset(preset));
  };

  const businessSummary = overview?.businessSummary;
  const financial = overview?.financialSummary || {};
  const position = overview?.position || {};
  const controls = overview?.controls || {};
  const ledger = overview?.ledger || {};
  const recent = overview?.recentMovements || [];
  const currency = overview?.currency || "ARS";
  const balanced = ledger.balanced !== false;
  const resultPositive = Number(businessSummary?.netProfit || 0) >= 0;
  const grossProfitPositive = Number(financial.grossProfit || 0) >= 0;
  const periodLabel = `${formatReportDate(overview?.period?.from || range.dateFrom)} al ${formatReportDate(overview?.period?.to || range.dateTo)}`;

  return (
    <Stack spacing={{ xs: 1.8, sm: 2.2, md: 2.6 }} sx={{ minWidth: 0 }}>
      {error ? (
        <Alert severity="error" action={<Button color="inherit" onClick={load}>Reintentar</Button>}>
          {error}
        </Alert>
      ) : null}

      <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" spacing={{ xs: 1.4, md: 2.2 }} alignItems={{ xs: "stretch", lg: "flex-start" }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography
              component="h2"
              sx={{
                fontWeight: 950,
                color: ERP_BRAND.dark,
                fontSize: { xs: "1.55rem", sm: "1.9rem", md: "2.35rem" },
                lineHeight: 1.1,
                letterSpacing: { xs: "-.35px", md: "-.65px" },
              }}
            >
              Resumen contable
            </Typography>
            <Chip icon={<AssessmentRoundedIcon />} label="Estado de resultados" color="success" size="small" sx={{ fontWeight: 850 }} />
          </Stack>
          <Typography sx={{ color: ERP_BRAND.muted, mt: 0.6, fontSize: { xs: 13.5, sm: 15 }, lineHeight: 1.5 }}>
            Entendé cuánto entra, cuánto cuesta operar y cuánto queda realmente en el negocio.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshRoundedIcon />}
          onClick={load}
          disabled={loading}
          sx={{
            alignSelf: { xs: "stretch", sm: "flex-start", lg: "center" },
            minHeight: 42,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 850,
          }}
        >
          Actualizar números
        </Button>
      </Stack>

      <ErpPanel>
        <CardContent sx={{ p: { xs: 1.6, sm: 2.2 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "190px minmax(150px, 1fr) minmax(150px, 1fr) auto" },
              gap: { xs: 1, sm: 1.3 },
              alignItems: "center",
            }}
          >
            <TextField select size="small" label="Período" value={range.preset} onChange={(event) => selectPreset(event.target.value)} sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
              <MenuItem value="today">Hoy</MenuItem>
              <MenuItem value="week">Últimos 7 días</MenuItem>
              <MenuItem value="month">Mes actual</MenuItem>
              <MenuItem value="30days">Últimos 30 días</MenuItem>
              <MenuItem value="custom">Personalizado</MenuItem>
            </TextField>
            <TextField size="small" type="date" label="Desde" value={range.dateFrom} onChange={(event) => setRange((current) => ({ ...current, preset: "custom", dateFrom: event.target.value }))} InputLabelProps={{ shrink: true }} inputProps={{ max: range.dateTo || undefined }} sx={{ minWidth: 0 }} />
            <TextField size="small" type="date" label="Hasta" value={range.dateTo} onChange={(event) => setRange((current) => ({ ...current, preset: "custom", dateTo: event.target.value }))} InputLabelProps={{ shrink: true }} inputProps={{ min: range.dateFrom || undefined }} sx={{ minWidth: 0 }} />
            <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, px: { xs: 0.35, md: 0 }, textAlign: { xs: "left", md: "right" } }}>
              <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, fontWeight: 850 }}>PERÍODO ANALIZADO</Typography>
              <Typography variant="body2" sx={{ color: ERP_BRAND.dark, fontWeight: 900 }}>{periodLabel}</Typography>
            </Box>
          </Box>
        </CardContent>
      </ErpPanel>

      <AccountingPeriodControl projectId={projectId} access={access} />

      {!loading ? (
        <BusinessHealthCenter
          health={businessHealth}
          error={healthError}
          currency={currency}
        />
      ) : null}

      {loading ? <ErpLoading label="Cargando el resumen consolidado del negocio..." /> : !businessSummary ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Tu rol puede ver el resumen operativo, pero no los importes contables. Para acceder a ganancias, pérdidas y saldos necesitás visibilidad financiera completa y permiso de lectura del libro contable.
        </Alert>
      ) : (
        <>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: { xs: 3.5, md: 5 },
              p: { xs: 2.1, sm: 2.7, md: 4 },
              color: "#fff",
              background: resultPositive
                ? "linear-gradient(135deg, #132A27 0%, #176F4C 52%, #21A365 100%)"
                : "linear-gradient(135deg, #2D1A1A 0%, #8E3030 55%, #C84545 100%)",
              boxShadow: resultPositive
                ? "0 24px 55px rgba(21,148,85,0.23)"
                : "0 24px 55px rgba(200,58,58,0.22)",
            }}
          >
            <Box sx={{ position: "absolute", width: 330, height: 330, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.07)", right: -110, top: -170 }} />
            <Box sx={{ position: "absolute", width: 180, height: 180, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.05)", right: 180, bottom: -130 }} />
            <Grid container spacing={3} sx={{ position: "relative" }}>
              <Grid item xs={12} lg={6.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {resultPositive ? <TrendingUpRoundedIcon /> : <TrendingDownRoundedIcon />}
                  <Typography sx={{ fontWeight: 900, letterSpacing: 0.4 }}>RESULTADO NETO DEL PERÍODO</Typography>
                </Stack>
                <Typography sx={{ fontSize: { xs: 32, sm: 48, md: 62 }, lineHeight: 1.05, fontWeight: 950, mt: 1.35, letterSpacing: { xs: -1.1, md: -2 }, overflowWrap: "anywhere" }}>
                  {formatMoney(businessSummary.netProfit, currency)}
                </Typography>
                <Typography sx={{ mt: 1.2, color: "rgba(255,255,255,0.82)", fontSize: { xs: 13.5, sm: 16 }, lineHeight: 1.5, maxWidth: 620 }}>
                  {resultPositive
                    ? `Después de cubrir costos y gastos, el negocio conservó ${formatMoney(businessSummary.netProfit, currency)}.`
                    : `Los costos y gastos superaron los ingresos por ${formatMoney(Math.abs(businessSummary.netProfit), currency)}.`}
                </Typography>
                <ComparisonChip metric={financial.comparison?.netProfit} sx={{ mt: 2, bgcolor: "rgba(255,255,255,0.13)", color: "#fff", "& .MuiChip-icon": { color: "#fff" } }} />
              </Grid>
              <Grid item xs={12} lg={5.5}>
                <Box sx={{ p: { xs: 1.7, sm: 2.4 }, borderRadius: { xs: 3, sm: 4 }, bgcolor: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.13)", backdropFilter: "blur(8px)" }}>
                  <Stack spacing={1.8}>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Box><Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)", fontWeight: 850 }}>INGRESOS</Typography><Typography sx={{ fontWeight: 950, fontSize: 21 }}>{formatMoney(businessSummary.revenue, currency)}</Typography></Box>
                      <Box sx={{ textAlign: "right" }}><Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)", fontWeight: 850 }}>COSTOS DIRECTOS</Typography><Typography sx={{ fontWeight: 950, fontSize: 21 }}>{formatMoney(businessSummary.directCosts, currency)}</Typography></Box>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Box><Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)", fontWeight: 850 }}>GASTOS OPERATIVOS</Typography><Typography sx={{ fontWeight: 950, fontSize: 21 }}>{formatMoney(businessSummary.operatingExpenses, currency)}</Typography></Box>
                      <Box sx={{ textAlign: "right" }}><Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)", fontWeight: 850 }}>RESULTADO</Typography><Typography sx={{ fontWeight: 950, fontSize: 21 }}>{formatMoney(businessSummary.netProfit, currency)}</Typography></Box>
                    </Stack>
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.13)" }} />
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Box><Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)" }}>Margen bruto</Typography><Typography sx={{ fontWeight: 950 }}>{formatPercent(financial.grossMargin)}</Typography></Box>
                      <Box sx={{ textAlign: "right" }}><Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)" }}>Margen neto</Typography><Typography sx={{ fontWeight: 950 }}>{formatPercent(financial.netMargin)}</Typography></Box>
                    </Stack>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} xl={3}>
              <SummaryMetric icon={<TrendingUpRoundedIcon />} label="INGRESOS" value={formatMoney(businessSummary.revenue, currency)} description="Ventas y otros ingresos reconocidos contablemente." metric={financial.comparison?.revenue} tone="green" />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <SummaryMetric icon={<ShoppingCartCheckoutRoundedIcon />} label="COSTOS DIRECTOS" value={formatMoney(businessSummary.directCosts, currency)} description="Mercadería, compras y costos directamente ligados a vender." metric={financial.comparison?.directCosts} inverse tone="orange" />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <SummaryMetric icon={<ReceiptLongRoundedIcon />} label="GASTOS OPERATIVOS" value={formatMoney(businessSummary.operatingExpenses, currency)} description="Egresos generales necesarios para mantener el negocio." metric={financial.comparison?.operatingExpenses} inverse tone="purple" />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <SummaryMetric icon={resultPositive ? <SavingsRoundedIcon /> : <TrendingDownRoundedIcon />} label="RESULTADO NETO" value={formatMoney(businessSummary.netProfit, currency)} description={`Resultado consolidado del período · margen ${formatPercent(financial.netMargin)}.`} metric={financial.comparison?.netProfit} tone={resultPositive ? "blue" : "red"} />
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={7.3}>
              <ErpPanel sx={{ height: "100%" }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <ErpSectionTitle title="Evolución del resultado" description="Cómo se movieron los ingresos y los gastos día por día." action={<Definition text="La distancia entre ambas líneas muestra cuánto margen generó o perdió el negocio cada día." />} />
                  <Box sx={{ mt: 2.2 }}><TrendChart data={overview?.financialTrend || []} currency={currency} /></Box>
                </CardContent>
              </ErpPanel>
            </Grid>
            <Grid item xs={12} lg={4.7}>
              <ErpPanel sx={{ height: "100%", bgcolor: ERP_BRAND.soft }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <ErpSectionTitle title="Cómo se forma la ganancia" description="Lectura simple del estado de resultados." />
                  <Stack spacing={1.5} sx={{ mt: 2.3 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={2}><Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: PROFIT }} /><Typography sx={{ fontWeight: 850 }}>Ingresos</Typography></Stack><Typography sx={{ fontWeight: 950 }}>{formatMoney(businessSummary.revenue, currency)}</Typography></Stack>
                    <Stack direction="row" justifyContent="space-between" spacing={2}><Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: COST }} /><Typography sx={{ fontWeight: 850 }}>− Costos directos</Typography></Stack><Typography sx={{ fontWeight: 950 }}>{formatMoney(businessSummary.directCosts, currency)}</Typography></Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between" spacing={2}><Typography sx={{ fontWeight: 950 }}>Ganancia bruta</Typography><Typography sx={{ fontWeight: 950, color: grossProfitPositive ? PROFIT : LOSS }}>{formatMoney(financial.grossProfit, currency)}</Typography></Stack>
                    <Stack direction="row" justifyContent="space-between" spacing={2}><Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: PURPLE }} /><Typography sx={{ fontWeight: 850 }}>− Gastos operativos</Typography></Stack><Typography sx={{ fontWeight: 950 }}>{formatMoney(businessSummary.operatingExpenses, currency)}</Typography></Stack>
                    <Box sx={{ p: 2, borderRadius: 3, bgcolor: resultPositive ? "rgba(21,148,85,0.1)" : "rgba(200,58,58,0.1)", border: `1px solid ${resultPositive ? "rgba(21,148,85,0.2)" : "rgba(200,58,58,0.2)"}` }}>
                      <Stack direction="row" justifyContent="space-between" spacing={2}><Typography sx={{ fontWeight: 950 }}>Resultado neto</Typography><Typography sx={{ fontWeight: 950, color: resultPositive ? PROFIT : LOSS }}>{formatMoney(businessSummary.netProfit, currency)}</Typography></Stack>
                    </Box>
                    <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                      Ganancia no significa necesariamente efectivo disponible: una venta pendiente suma al resultado, pero todavía forma parte de cuentas por cobrar.
                    </Alert>
                  </Stack>
                </CardContent>
              </ErpPanel>
            </Grid>
          </Grid>

          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.6 }}>
              <Typography variant="h4" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Posición financiera consolidada al cierre</Typography>
              <Definition text="Estos saldos se calculan desde el libro contable acumulado hasta el último día del período." />
            </Stack>
            <Grid container spacing={1.7}>
              <Grid item xs={12} sm={6} lg={4} xl={2}><PositionCard icon={<AccountBalanceWalletRoundedIcon />} label="CAJA Y BANCOS" value={formatMoney(businessSummary.cash, currency)} detail={`${overview?.openCashSessions || 0} cajas abiertas · ${registers.length || bootstrap.registers.length || 0} configuradas`} tone="green" /></Grid>
              <Grid item xs={12} sm={6} lg={4} xl={2}><PositionCard icon={<TrendingUpRoundedIcon />} label="POR COBRAR" value={formatMoney(position.receivables, currency)} detail="Dinero vendido a cuenta que todavía debe ingresar." tone="blue" /></Grid>
              <Grid item xs={12} sm={6} lg={4} xl={2}><PositionCard icon={<TrendingDownRoundedIcon />} label="DEUDA TOTAL" value={formatMoney(businessSummary.debt, currency)} detail="Total acumulado de obligaciones registradas en cuentas de pasivo." tone="red" /></Grid>
              <Grid item xs={12} sm={6} lg={4} xl={2}><PositionCard icon={<Inventory2RoundedIcon />} label="INVENTARIO" value={formatMoney(businessSummary.inventory, currency)} detail="Valor contable acumulado de mercadería disponible." tone="purple" /></Grid>
              <Grid item xs={12} sm={6} lg={4} xl={2}><PositionCard icon={<ReceiptLongRoundedIcon />} label="IMPUESTOS NETOS" value={formatMoney(position.netTaxPosition, currency)} detail={Number(position.netTaxPosition || 0) >= 0 ? "Saldo estimado a pagar luego de créditos fiscales." : "Crédito fiscal superior a impuestos por pagar."} tone="orange" /></Grid>
              <Grid item xs={12} sm={6} lg={4} xl={2}><PositionCard icon={<SavingsRoundedIcon />} label="CAPITAL DE TRABAJO" value={formatMoney(position.workingCapital, currency)} detail="Activos menos obligaciones registradas en el libro." tone={Number(position.workingCapital || 0) >= 0 ? "dark" : "red"} /></Grid>
            </Grid>
          </Box>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={6}>
              <AccountBreakdown title="De dónde vienen los ingresos" description="Cuentas que explican la facturación y otros ingresos." rows={financial.incomeAccounts || []} total={businessSummary.revenue} color={PROFIT} emptyText="Sin ingresos contabilizados" currency={currency} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <AccountBreakdown title="En qué se va el dinero" description="Costos y gastos que reducen el resultado del negocio." rows={financial.expenseAccounts || []} total={financial.totalExpenses} color={COST} emptyText="Sin costos ni gastos contabilizados" currency={currency} />
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={5}>
              <ErpPanel sx={{ height: "100%" }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <ErpSectionTitle title="Salud contable" description="Controles que requieren atención antes de confiar en los números." />
                  <Stack spacing={1.3} sx={{ mt: 2.2 }}>
                    {Number(ledger.entries || 0) === 0 ? (
                      <HealthAlert severity="info" icon={<InfoOutlinedIcon />} title="Todavía no hay asientos en el período" description="Confirmá una operación para comenzar a construir el estado contable." />
                    ) : !balanced ? (
                      <HealthAlert severity="error" icon={<ErrorOutlineRoundedIcon />} title="Debe y haber no coinciden" description={`Debe ${formatMoney(ledger.debits, currency)} y haber ${formatMoney(ledger.credits, currency)}. Revisá el libro antes de cerrar el período.`} action={canReadLedger ? <Button color="inherit" size="small" onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/ledger`)}>Revisar</Button> : null} />
                    ) : (
                      <HealthAlert severity="success" icon={<CheckCircleRoundedIcon />} title="Libro balanceado" description={`${ledger.entries || 0} líneas contables y la doble partida coincide en el período.`} />
                    )}
                    {Number(controls.confirmedWithoutLedger || 0) > 0 ? (
                      <HealthAlert severity="error" icon={<ErrorOutlineRoundedIcon />} title={`${controls.confirmedWithoutLedger} operaciones sin asiento`} description="Son movimientos confirmados que todavía no impactaron correctamente en el libro." action={<Button color="inherit" size="small" onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos`)}>Ver</Button>} />
                    ) : (
                      <HealthAlert severity="success" icon={<CheckCircleRoundedIcon />} title="Operaciones contabilizadas" description="No se detectaron movimientos confirmados sin aplicación contable." />
                    )}
                    {Number(controls.draftMovements || 0) > 0 ? <HealthAlert severity="info" icon={<InfoOutlinedIcon />} title={`${controls.draftMovements} borradores pendientes`} description="Los borradores no forman parte de ganancias, caja ni inventario hasta ser confirmados." /> : null}
                    {Number(position.cashCoverage || 0) < 100 && Number(position.payables || 0) > 0 ? <HealthAlert severity="warning" icon={<ErrorOutlineRoundedIcon />} title="Caja menor que las deudas registradas" description={`La liquidez cubre aproximadamente ${formatPercent(position.cashCoverage || 0)} de las cuentas por pagar.`} /> : null}
                  </Stack>
                </CardContent>
              </ErpPanel>
            </Grid>
            <Grid item xs={12} lg={7}>
              <ErpPanel sx={{ height: "100%" }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <ErpSectionTitle title="Últimos movimientos" description="Operaciones recientes que alimentan este resumen." action={<Button size="small" onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos`)} sx={{ textTransform: "none", fontWeight: 850 }}>Ver historial</Button>} />
                  {!recent.length ? (
                    <ErpEmpty title="Todavía no hay movimientos" description="Creá una venta, compra, gasto o entrada de caja para comenzar el historial." action={canCreateMovement ? <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos?nuevo=1`)} sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 850 }}>Crear movimiento</Button> : null} />
                  ) : (
                    <Stack spacing={0} sx={{ mt: 1.6 }}>
                      {recent.slice(0, 7).map((movement, index) => (
                        <Stack key={movement.id || movement._id} direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.3} sx={{ py: 1.55, borderTop: index ? `1px solid ${ERP_BRAND.border}` : "none" }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>{movement.movementNumber}</Typography>
                              <Chip size="small" label={MOVEMENT_KIND_LABELS[movement.kind] || movement.kind} />
                              <MovementStatusChip status={movement.status} />
                            </Stack>
                            <Typography noWrap variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.5 }}>{movement.description || movement.party?.name || "Sin descripción"}</Typography>
                          </Box>
                          <Box sx={{ textAlign: { xs: "left", sm: "right" }, flexShrink: 0 }}>
                            <Typography sx={{ fontWeight: 950 }}>{formatMoney(movement.total, movement.currency)}</Typography>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{formatDateTime(movement.occurredAt)}</Typography>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </ErpPanel>
            </Grid>
          </Grid>

          <ErpPanel sx={{ bgcolor: ERP_BRAND.dark, color: "#fff" }}>
            <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2.3} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 950 }}>Registrar bien es entender mejor el negocio</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.68)", mt: 0.5 }}>Cada venta, compra, gasto, cobro y pago confirmado actualiza automáticamente ganancias, saldos y alertas.</Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
                  {canCreateMovement ? <Button variant="contained" startIcon={<ReceiptLongRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos?nuevo=1`)} sx={{ bgcolor: "#fff", color: ERP_BRAND.dark, textTransform: "none", fontWeight: 900, borderRadius: 2.5, "&:hover": { bgcolor: "rgba(255,255,255,0.9)" } }}>Nueva operación</Button> : null}
                  {canReadCash ? <Button variant="outlined" startIcon={<PointOfSaleRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/caja`)} sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)", textTransform: "none", fontWeight: 850, borderRadius: 2.5 }}>Ir a caja</Button> : null}
                  {canReadLedger ? <Button variant="outlined" startIcon={<AccountBalanceRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/ledger`)} sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.35)", textTransform: "none", fontWeight: 850, borderRadius: 2.5 }}>Abrir libro</Button> : null}
                </Stack>
              </Stack>
            </CardContent>
          </ErpPanel>
        </>
      )}
    </Stack>
  );
}
